import { duration } from '../dateUtils';
import AnalyticsProperties from './AnalyticsProperties';
import { trackFirstScreenTap } from './events';

/** @returns `payload` without the properties that are not social event properties. */
export function excludePayloadOnlyProperties<
  T extends AnalyticsProperties.Payload,
>(payload: T): Omit<T, AnalyticsProperties.PayloadOnlyProperties> {
  const { sourceChainTimestamps, tournamentAPI, ...rest } = payload;

  return rest;
}

export function getNextGenerationProperties(
  entryData: AnalyticsProperties.EntryData,
  now: number,
  opts: { channel: AnalyticsProperties.Channel; playerID: string },
): AnalyticsProperties.GenerationProperties {
  if (entryData.$zeroEntryDatetime != null) {
    // Continue an existing referral chain: copy original referral properties
    // and increase generation by one.
    return {
      $firstEntryGeneration: (+entryData.$firstEntryGeneration || 0) + 1,

      $zeroEntryChannel: entryData.$zeroEntryChannel,
      $zeroEntryDatetime: entryData.$zeroEntryDatetime,
      $zeroEntryDate: entryData.$zeroEntryDate,

      $zeroEntryAdCampaignName: entryData.$zeroEntryAdCampaignName,
      $zeroEntryAdSetName: entryData.$zeroEntryAdSetName,
      $zeroEntryAdCreativeName: entryData.$zeroEntryAdCreativeName,
      $zeroEntryAdNetworkName: entryData.$zeroEntryAdNetworkName,
      zeroEntrySourcePlayerID: entryData.zeroEntrySourcePlayerID,
    };
  } else {
    // Start a new chain of referrals:
    const datetime = new Date(now).toISOString();

    return {
      $firstEntryGeneration: 0,

      $zeroEntryChannel: opts.channel,
      $zeroEntryDatetime: datetime,
      $zeroEntryDate: datetime.slice(0, 10),

      $zeroEntryAdCampaignName: entryData.$adCampaignName,
      $zeroEntryAdSetName: entryData.$adSetName,
      $zeroEntryAdCreativeName: entryData.$adCreativeName,
      $zeroEntryAdNetworkName: entryData.$adNetworkName,
      zeroEntrySourcePlayerID: opts.playerID,
    };
  }
}

export function getSessionMaturityRange(entryCount: number): string {
  if (entryCount <= 1) return '1';
  else if (entryCount <= 2) return '2';
  else if (entryCount <= 5) return '3-5';
  else if (entryCount <= 9) return '6-9';
  else if (entryCount <= 19) return '10-19';
  else if (entryCount <= 49) return '20-49';
  else if (entryCount <= 99) return '50-99';
  else if (entryCount <= 199) return '100-199';
  else if (entryCount <= 399) return '200-399';
  else if (entryCount <= 799) return '400-799';
  else return '800+';
}

/**
 * Attaches a listener to 'pointerdown' events in order to track a user's first actual tap.
 * Should be called as part of `startGameAsync`.
 *
 * @internal
 */
export function attachFirstTapListener(): void {
  const firstScreenTapListener = () => {
    trackFirstScreenTap({ occasion: 'sessionStart' });

    window.removeEventListener('pointerdown', firstScreenTapListener);
  };

  window.addEventListener('pointerdown', firstScreenTapListener);
}

/**
 * Return entry data condensed into the minimum set of properties that uniquely
 * identifies the entry point.
 */
export function entryDataToPayloadIdentifierObject(
  entryData: AnalyticsProperties.EntryData,
  entryPointName: string,
): { [key: string]: any } {
  const identifyingKeys = [
    '$adCampaignName',
    '$adCreativeName',
    '$adSetName',
    '$channel',
    '$creativeAssetID',
    '$pageLinkId',
    '$subFeature',
    'creativeAssetID',
    'feature',
    'playerID',
    'subFeature',
    'tracerKey',
  ];

  const result = {};

  for (const key of identifyingKeys) {
    const value = entryData[key];
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  }

  if (entryPointName) {
    result['entryPointName'] = entryPointName;
  }

  return result;
}

type ReferralChainLengths = {
  [key in AnalyticsProperties.ChainLength]:
    | number
    | AnalyticsProperties.AmplitudeNullValue;
};

export function getReferralChainLengths(
  referralChainTimestamps: number[],
  now: number,
): ReferralChainLengths {
  const durations: { [key in AnalyticsProperties.ChainLength]: number } = {
    '1M': duration({ minutes: 1 }),
    '5M': duration({ minutes: 5 }),
    '15M': duration({ minutes: 15 }),
    '30M': duration({ minutes: 30 }),
    '1H': duration({ hours: 1 }),
    '2H': duration({ hours: 2 }),
    '4H': duration({ hours: 4 }),
    '8H': duration({ hours: 8 }),
    '12H': duration({ hours: 12 }),
    '1D': duration({ hours: 24 }),
  };

  return Object.entries(durations).reduce((acc, [key, interval]) => {
    acc[key] =
      referralChainTimestamps.filter((timestamp) => now - timestamp <= interval)
        .length || '(none)';

    return acc;
  }, {} as ReferralChainLengths);
}

export function isSocialChannel(channel: AnalyticsProperties.Channel): boolean {
  const socialChannels: AnalyticsProperties.Channel[] = [
    'TOURNAMENT',
    'INVITE',
    'SHARE',
    'UPDATE',
  ];
  return socialChannels.includes(channel);
}
